import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BackButton } from '@/components/BackButton';
import { useAuth } from '@/lib/auth';
import { RaptorLogo } from '@/components/RaptorLogo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NotificationBell } from '@/components/NotificationBell';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SideNav, NavItem, useSidebarState } from '@/components/SideNav';
import {
  LayoutDashboard, Users, Briefcase, FileText, MapPin, ShieldCheck, Settings,
  Activity, LogOut, UserCircle2, ClipboardCheck, BarChart, FilePlus, Edit3, ShieldAlert, Download, Bell, DatabaseBackup, ClipboardList,
} from 'lucide-react';

const Item: React.FC<{ to: string; icon: React.ReactNode; label: string; end?: boolean }> = ({ to, icon, label, end }) => (
  <NavItem to={to} end={end} label={label} icon={icon}
    activeClass="raptor-sidebar-item-active font-semibold border-l-2 border-raptor-blue"
    idleClass="text-raptor-navy/70 hover:bg-raptor-neutral/60" />
);

export const InsurerLayout: React.FC = () => {
  const { user, role, logout, impersonating, exitImpersonation } = useAuth();
  const nav = useNavigate();
  const doLogout = async (): Promise<void> => { await logout(); nav('/login', { replace: true }); };
  const doExitImpersonation = async (): Promise<void> => {
    toast.dismiss();  // clear the "Now operating as…" toast so it can't overlap
    try { await exitImpersonation(); nav('/master', { replace: true }); }
    catch {
      toast.error('Could not return to the Master Portal — please sign in again.');
      await logout(); nav('/login', { replace: true });
    }
  };
  const isSuper = role?.key === 'super_admin';
  const { collapsed, toggle } = useSidebarState();

  return (
    <div className="flex-1 flex bg-background overflow-hidden">
      <SideNav collapsed={collapsed} toggle={toggle}
        className="raptor-sidebar-themed border-r border-border/70 shadow-sm"
        toggleClassName="bg-white border border-border/70 text-raptor-navy hover:bg-raptor-neutral/60">
        <div className="h-16 px-4 py-2 flex items-center justify-center border-b border-border/60 flex-shrink-0">
          <RaptorLogo size={40} slot="sidebar_logo" />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
          <Item to="/insurer" icon={<LayoutDashboard size={16} />} label="Dashboard" end />
          <Item to="/insurer/brokers" icon={<Users size={16} />} label="Brokers" />
          <Item to="/insurer/clients" icon={<Briefcase size={16} />} label="Clients" />
          <Item to="/insurer/sites" icon={<MapPin size={16} />} label="Sites" />
          <Item to="/insurer/pending-sites" icon={<ClipboardList size={16} />} label="Pending Sites" />
          <Item to="/insurer/templates" icon={<Edit3 size={16} />} label="Survey Templates" />
          <Item to="/insurer/instructions" icon={<ClipboardCheck size={16} />} label="Instructions" />
          <Item to="/insurer/imports" icon={<FilePlus size={16} />} label="Imports" />
          <Item to="/insurer/risk" icon={<ShieldAlert size={16} />} label="Risk Ratings" />
          <Item to="/insurer/exports" icon={<Download size={16} />} label="Bordereaux" />
          <Item to="/insurer/analytics" icon={<BarChart size={16} />} label="Analytics" />
          <Item to="/insurer/alerts" icon={<Bell size={16} />} label="Alerts" />
          <Item to="/insurer/covers" icon={<FileText size={16} />} label="Confirmations of Cover" />
          <Item to="/insurer/audit" icon={<ShieldCheck size={16} />} label="Audit Log" />
          <Item to="/insurer/system-health" icon={<Activity size={16} />} label="System Health" />
          {isSuper && <Item to="/insurer/backups" icon={<DatabaseBackup size={16} />} label="Backups" />}
          <Item to="/insurer/settings" icon={<Settings size={16} />} label="Settings" />
        </nav>
        <div className="p-3 border-t border-border/60 flex-shrink-0">
          <NavLink to="/insurer/profile" title={collapsed ? user?.full_name : undefined}
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2'} p-2 rounded-md hover:bg-raptor-neutral/60`}>
            <UserCircle2 size={22} className="text-raptor-blue flex-shrink-0" />
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-raptor-navy truncate">{user?.full_name}</div>
                <div className="text-[11px] text-raptor-navy/60 truncate">{role?.name}</div>
              </div>
            )}
          </NavLink>
          <Button variant="ghost" size="sm" className={`w-full mt-2 ${collapsed ? 'justify-center px-0' : 'justify-start'}`} onClick={doLogout} title={collapsed ? 'Sign out' : undefined}>
            <LogOut size={16} /> {!collapsed && 'Sign out'}
          </Button>
        </div>
      </SideNav>
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <header className="raptor-header-themed border-b border-border/70 h-16 px-6 flex items-center justify-between flex-shrink-0 z-10">
          <div className="flex items-center gap-3">
            <BackButton />
            <div>
              <h1 className="font-headline text-lg tracking-wide leading-tight" style={{ color: 'var(--raptor-header-text)' }}>Insurer Portal</h1>
              <p className="text-xs font-body opacity-70 leading-tight" style={{ color: 'var(--raptor-header-text)' }}>{user?.insurer_name || 'Insurer'}{user?.insurer_id ? ` · ${user.insurer_id}` : ''}</p>
            </div>
            {impersonating && (
              <Button size="sm" variant="outline" className="ml-2" onClick={doExitImpersonation} data-testid="exit-impersonation-btn">
                <LogOut size={14} /> Return to Master Portal
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {impersonating && <Badge variant="warning">Impersonating</Badge>}
            {isSuper && <Badge variant="navy">Super Admin</Badge>}
            {role?.key === 'insurer_admin' && <Badge variant="blue">Insurer Admin</Badge>}
            <ThemeToggle />
            <NotificationBell />
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
